import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { DSessionListItem, FoolscapSummary, DrafterSpec } from '@/lib/api'
import {
  listDSessions,
  createDSession,
  getDSessionWithTurns,
  deleteDSession as apiDeleteDSession,
  updateDSession,
  listAvailableFoolscaps,
} from '@/lib/api'

console.log('[d-store] Module loading...')

// =============================================================================
// CRITICAL: User isolation check - runs SYNCHRONOUSLY before store hydration
// =============================================================================

const D_USER_ID_KEY = 'd-store-user-id'

function clearStoreIfUserMismatch(): void {
  try {
    const storedUserId = localStorage.getItem(D_USER_ID_KEY)
    const hasDStore = localStorage.getItem('d-store') !== null

    console.log('[d-store] SYNC: Checking user mismatch')
    console.log('[d-store] SYNC: Stored user ID:', storedUserId)
    console.log('[d-store] SYNC: Has d-store data:', hasDStore)

    // Find Supabase auth token in localStorage
    let currentUserId: string | null = null

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.includes('-auth-token')) {
        try {
          const rawValue = localStorage.getItem(key)
          const authData = JSON.parse(rawValue || '{}')
          currentUserId = authData?.user?.id || authData?.session?.user?.id || null
          if (currentUserId) break
        } catch (e) {
          // Invalid JSON, skip
        }
      }
    }

    // CASE 1: We have a stored user ID and it doesn't match current user
    if (storedUserId && currentUserId && storedUserId !== currentUserId) {
      console.log('[d-store] SYNC: User mismatch detected! Clearing stale data')
      localStorage.removeItem('d-store')
      localStorage.setItem(D_USER_ID_KEY, currentUserId)
    }
    // CASE 2: No stored user ID but we have d-store data AND a current user
    else if (!storedUserId && hasDStore && currentUserId) {
      console.log('[d-store] SYNC: No stored user ID but d-store exists. Clearing orphaned data.')
      localStorage.removeItem('d-store')
      localStorage.setItem(D_USER_ID_KEY, currentUserId)
    }
    // CASE 3: We have a current user but no stored user ID (fresh start)
    else if (!storedUserId && currentUserId) {
      console.log('[d-store] SYNC: New user, storing user ID')
      localStorage.setItem(D_USER_ID_KEY, currentUserId)
    }
    // CASE 4: No current user (not logged in)
    else if (!currentUserId) {
      console.log('[d-store] SYNC: No Supabase auth token found in localStorage')
      if (hasDStore) {
        console.log('[d-store] SYNC: Clearing orphaned d-store (no logged in user)')
        localStorage.removeItem('d-store')
        localStorage.removeItem(D_USER_ID_KEY)
      }
    }
  } catch (e) {
    console.error('[d-store] Error checking user mismatch:', e)
  }
}

// Run the check immediately when this module is loaded
clearStoreIfUserMismatch()

// =============================================================================
// Types
// =============================================================================

export interface FinalDiffData {
  has_changes: boolean
  original_doc: string
  final_doc: string
  stats: {
    blocks_added: number
    blocks_deleted: number
    blocks_modified: number
    blocks_unchanged: number
  }
  changes: Array<{
    type: 'added' | 'deleted' | 'modified'
    content?: string
    modifications?: Array<{
      deleted: string
      added: string
      context: string
    }>
  }>
}

export interface Turn {
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  thinking?: string  // Extended thinking content
  zinsserThinking?: string
  strengthenVerbsThinking?: string
  checkLogicThinking?: string
  rhythmSoundThinking?: string
  responderThinking?: string
  // Node execution flags
  zinsserRan?: boolean
  strengthenVerbsRan?: boolean
  checkLogicRan?: boolean
  rhythmSoundRan?: boolean
  responderRan?: boolean
  // Final diff for this turn
  finalDiff?: FinalDiffData | null
  showFinalDiff?: boolean
  // Hidden flag - turn exists but not shown in UI (e.g., auto-generated initial turn)
  isHidden?: boolean
}

export interface VoiceDirection {
  hook_type?: 'deadpan_inversion' | 'permission_statement' | 'systemic_accusation'
  primary_mode?: 'deadpan' | 'earnest'
  framework_type?: 'numbered_set' | 'named_model' | 'dichotomy' | 'spectrum'
  kicker_style?: 'absurdist' | 'philosophical' | 'call_to_becoming' | 'dropped_mic'
  target_reader?: string
}

export interface GatekeeperStreamingContent {
  response: string
  session_id?: string
  thinking?: string
}

type WorkflowStatus = 'idle' | 'submitting' | 'processing' | 'streaming' | 'complete' | 'error'
export type DMode = 'gatekeeper' | 'draft'

// =============================================================================
// Store Interface
// =============================================================================

interface DState {
  // Session
  sessionId: string | null
  sessionName: string

  // Session list (for picker)
  sessions: DSessionListItem[]
  isLoadingSessions: boolean
  showSessionPicker: boolean

  // Foolscap selection
  availableFoolscaps: FoolscapSummary[]
  isLoadingFoolscaps: boolean
  showFoolscapPicker: boolean
  selectedFoolscapId: string | null
  foolscapSnapshot: string  // Read-only reference content

  // Session sidebar (left panel)
  showSessionSidebar: boolean

  // Mobile view state
  mobileView: 'sessions' | 'chat' | 'document'

  // D mode (gatekeeper | draft)
  dMode: DMode

  // Track if initial draft has run for current session (prevents re-run on refresh)
  hasInitialDraftRun: boolean

  // Flag set when session is created - DChat checks this once to trigger initial draft
  pendingInitialTrigger: boolean

  // Workflow status
  workflowStatus: WorkflowStatus
  currentTaskId: string | null
  error: string | null

  // Document state
  documentContent: string  // Annotated content for display
  documentCleanContent: string  // Clean content for next turn diff
  documentDisplayContent: string | null  // Transient display content during streaming

  // Voice direction (gathered from Gatekeeper)
  voiceDirection: VoiceDirection | null

  // Gatekeeper state (voice direction gathering)
  gatekeeperTurns: Turn[]
  gatekeeperStreamingContent: GatekeeperStreamingContent | null
  gatekeeperStreamingThinking: string | null
  gatekeeperIsThinking: boolean

  // Zinsser state (drafting with hybrid voice)
  zinsserDoc: string
  zinsserDiagnosis: {
    controlling_idea?: string
    audience_contract?: string
    structure_issues?: string[]
    evidence_gaps?: string[]
    opening_closing?: string
    voice_consistency?: string
  } | null
  zinsserChangesMade: string[]
  zinsserStreamingThinking: string | null
  zinsserIsThinking: boolean
  zinsserIsProcessing: boolean
  zinsserHasThinking: boolean

  // StrengthenVerbs state (micro editing - Pass 1)
  strengthenVerbsChangesMade: string[]
  strengthenVerbsStreamingThinking: string | null
  strengthenVerbsIsThinking: boolean
  strengthenVerbsIsProcessing: boolean
  strengthenVerbsHasThinking: boolean

  // CheckLogic state (micro editing - Pass 2)
  checkLogicChangesMade: string[]
  checkLogicStreamingThinking: string | null
  checkLogicIsThinking: boolean
  checkLogicIsProcessing: boolean
  checkLogicHasThinking: boolean

  // RhythmSound state (micro editing - Pass 3)
  rhythmSoundChangesMade: string[]
  rhythmSoundStreamingThinking: string | null
  rhythmSoundIsThinking: boolean
  rhythmSoundIsProcessing: boolean
  rhythmSoundHasThinking: boolean

  // Responder state (coaching response after drafting)
  responderResponse: string | null
  responderFollowUpQuestion: string | null
  responderStreamingThinking: string | null
  responderIsThinking: boolean
  responderIsProcessing: boolean
  responderHasThinking: boolean

  // Final diff state
  finalDiff: FinalDiffData | null
  showFinalDiff: boolean

  // Actions
  setDMode: (mode: DMode) => void
  setHasInitialDraftRun: (hasRun: boolean) => void
  clearPendingInitialTrigger: () => void
  setSessionName: (name: string) => void
  setWorkflowStatus: (status: WorkflowStatus) => void
  setCurrentTaskId: (taskId: string | null) => void
  setError: (error: string | null) => void
  startNewSession: () => void

  // Session management actions
  openSessionPicker: () => void
  closeSessionPicker: () => void
  openSessionSidebar: () => void
  closeSessionSidebar: () => void
  toggleSessionSidebar: () => void
  setMobileView: (view: 'sessions' | 'chat' | 'document') => void
  fetchSessions: () => Promise<void>
  loadSessionFromDb: (sessionId: string) => Promise<void>
  deleteSession: (sessionId: string) => Promise<void>
  renameSession: (name: string) => Promise<void>

  // Foolscap selection actions
  openFoolscapPicker: () => void
  closeFoolscapPicker: () => void
  fetchAvailableFoolscaps: () => Promise<void>
  selectFoolscap: (foolscapId: string) => void
  createSessionFromFoolscap: (foolscapId: string, name?: string, drafterSpec?: DrafterSpec) => Promise<void>

  // Document actions
  updateDocumentContent: (content: string) => void

  // Voice direction actions
  setVoiceDirection: (direction: VoiceDirection) => void

  // Gatekeeper actions
  addGatekeeperUserTurn: (content: string) => void
  startGatekeeperStreaming: () => void
  startGatekeeperThinking: () => void
  updateGatekeeperThinking: (delta: string) => void
  finishGatekeeperThinking: () => void
  finishGatekeeperStreaming: (output: GatekeeperStreamingContent) => void

  // Zinsser actions
  startZinsserProcessing: () => void
  startZinsserThinking: () => void
  updateZinsserThinking: (delta: string) => void
  finishZinsserThinking: () => void
  finishZinsserProcessing: (output: {
    zin_doc: string
    diagnosis: {
      controlling_idea?: string
      audience_contract?: string
      structure_issues?: string[]
      evidence_gaps?: string[]
      opening_closing?: string
      voice_consistency?: string
    }
    changes_made: string[]
  }) => void

  // StrengthenVerbs actions (micro editing - Pass 1)
  startStrengthenVerbsProcessing: () => void
  startStrengthenVerbsThinking: () => void
  updateStrengthenVerbsThinking: (delta: string) => void
  finishStrengthenVerbsThinking: () => void
  finishStrengthenVerbsProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // CheckLogic actions (micro editing - Pass 2)
  startCheckLogicProcessing: () => void
  startCheckLogicThinking: () => void
  updateCheckLogicThinking: (delta: string) => void
  finishCheckLogicThinking: () => void
  finishCheckLogicProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // RhythmSound actions (micro editing - Pass 3)
  startRhythmSoundProcessing: () => void
  startRhythmSoundThinking: () => void
  updateRhythmSoundThinking: (delta: string) => void
  finishRhythmSoundThinking: () => void
  finishRhythmSoundProcessing: (output: {
    zin_doc: string
    changes_made: string[]
  }) => void

  // Responder actions
  startResponderProcessing: () => void
  startResponderThinking: () => void
  updateResponderThinking: (delta: string) => void
  finishResponderThinking: () => void
  finishResponderProcessing: (output: {
    response: string
    follow_up_question?: string | null
  }) => void

  // Final diff actions
  setFinalDiff: (diff: FinalDiffData | null) => void
  showFinalDiffViewer: () => void
  hideFinalDiffViewer: () => void
  hideTurnDiffViewer: (turnIndex: number) => void

  // Real-time canvas editing
  applyCanvasEdit: (oldStr: string, newStr: string) => void
  applyCanvasInsert: (insertLine: number, insertText: string) => void
}

// =============================================================================
// Store Implementation
// =============================================================================

export const useDStore = create<DState>()(
  persist(
    (set, get) => ({
      // Initial state
      sessionId: null,
      sessionName: 'New Draft',
      sessions: [],
      isLoadingSessions: false,
      showSessionPicker: false,
      availableFoolscaps: [],
      isLoadingFoolscaps: false,
      showFoolscapPicker: false,
      selectedFoolscapId: null,
      foolscapSnapshot: '',
      showSessionSidebar: false,
      mobileView: 'chat',
      dMode: 'gatekeeper',
      hasInitialDraftRun: false,
      pendingInitialTrigger: false,
      workflowStatus: 'idle',
      currentTaskId: null,
      error: null,

      // Document state
      documentContent: '',
      documentCleanContent: '',
      documentDisplayContent: null,

      // Voice direction
      voiceDirection: null,

      // Gatekeeper state
      gatekeeperTurns: [],
      gatekeeperStreamingContent: null,
      gatekeeperStreamingThinking: null,
      gatekeeperIsThinking: false,

      // Zinsser state
      zinsserDoc: '',
      zinsserDiagnosis: null,
      zinsserChangesMade: [],
      zinsserStreamingThinking: null,
      zinsserIsThinking: false,
      zinsserIsProcessing: false,
      zinsserHasThinking: false,

      // StrengthenVerbs state
      strengthenVerbsChangesMade: [],
      strengthenVerbsStreamingThinking: null,
      strengthenVerbsIsThinking: false,
      strengthenVerbsIsProcessing: false,
      strengthenVerbsHasThinking: false,

      // CheckLogic state
      checkLogicChangesMade: [],
      checkLogicStreamingThinking: null,
      checkLogicIsThinking: false,
      checkLogicIsProcessing: false,
      checkLogicHasThinking: false,

      // RhythmSound state
      rhythmSoundChangesMade: [],
      rhythmSoundStreamingThinking: null,
      rhythmSoundIsThinking: false,
      rhythmSoundIsProcessing: false,
      rhythmSoundHasThinking: false,

      // Responder state
      responderResponse: null,
      responderFollowUpQuestion: null,
      responderStreamingThinking: null,
      responderIsThinking: false,
      responderIsProcessing: false,
      responderHasThinking: false,

      // Final diff state
      finalDiff: null,
      showFinalDiff: false,

      // Basic actions
      setDMode: (mode) => set({ dMode: mode }),
      setHasInitialDraftRun: (hasRun) => set({ hasInitialDraftRun: hasRun }),
      clearPendingInitialTrigger: () => set({ pendingInitialTrigger: false }),
      setSessionName: (name) => set({ sessionName: name }),
      setWorkflowStatus: (status) => set({ workflowStatus: status }),
      setCurrentTaskId: (taskId) => set({ currentTaskId: taskId }),
      setError: (error) => set({ error }),

      startNewSession: () => set({
        sessionId: null,
        sessionName: 'New Draft',
        selectedFoolscapId: null,
        foolscapSnapshot: '',
        dMode: 'gatekeeper',
        pendingInitialTrigger: false,
        workflowStatus: 'idle',
        currentTaskId: null,
        error: null,
        documentContent: '',
        documentCleanContent: '',
        documentDisplayContent: null,
        voiceDirection: null,
        gatekeeperTurns: [],
        gatekeeperStreamingContent: null,
        gatekeeperStreamingThinking: null,
        gatekeeperIsThinking: false,
        zinsserDoc: '',
        zinsserDiagnosis: null,
        zinsserChangesMade: [],
        zinsserStreamingThinking: null,
        zinsserIsThinking: false,
        zinsserIsProcessing: false,
        zinsserHasThinking: false,
        strengthenVerbsChangesMade: [],
        strengthenVerbsStreamingThinking: null,
        strengthenVerbsIsThinking: false,
        strengthenVerbsIsProcessing: false,
        strengthenVerbsHasThinking: false,
        checkLogicChangesMade: [],
        checkLogicStreamingThinking: null,
        checkLogicIsThinking: false,
        checkLogicIsProcessing: false,
        checkLogicHasThinking: false,
        rhythmSoundChangesMade: [],
        rhythmSoundStreamingThinking: null,
        rhythmSoundIsThinking: false,
        rhythmSoundIsProcessing: false,
        rhythmSoundHasThinking: false,
        responderResponse: null,
        responderFollowUpQuestion: null,
        responderStreamingThinking: null,
        responderIsThinking: false,
        responderIsProcessing: false,
        responderHasThinking: false,
        finalDiff: null,
        showFinalDiff: false,
        showFoolscapPicker: true,  // Show picker for new session
      }),

      // Session management actions
      openSessionPicker: () => set({ showSessionPicker: true }),
      closeSessionPicker: () => set({ showSessionPicker: false }),
      openSessionSidebar: () => set({ showSessionSidebar: true }),
      closeSessionSidebar: () => set({ showSessionSidebar: false }),
      toggleSessionSidebar: () => set((state) => ({ showSessionSidebar: !state.showSessionSidebar })),
      setMobileView: (view) => set({ mobileView: view }),

      fetchSessions: async () => {
        set({ isLoadingSessions: true })
        try {
          const sessions = await listDSessions()
          set({ sessions, isLoadingSessions: false })
        } catch (error) {
          console.error('[d-store] Failed to fetch sessions:', error)
          set({ isLoadingSessions: false })
        }
      },

      loadSessionFromDb: async (sessionId: string) => {
        try {
          const session = await getDSessionWithTurns(sessionId)
          const turns = (session.turns || []).map((t: { role: string; content: string; timestamp: string; thinking?: string }) => ({
            role: t.role as 'user' | 'assistant',
            content: t.content,
            timestamp: t.timestamp,
            thinking: t.thinking,
          }))
          // Note: Backend saves final edited doc to old_doc (baseline for next diff)
          // So we load old_doc as the document content to display
          const docContent = session.old_doc || session.new_doc || ''
          set({
            sessionId: session.session_id,
            sessionName: session.name || 'Untitled Draft',
            selectedFoolscapId: session.source_foolscap_id || null,
            foolscapSnapshot: session.foolscap_snapshot || '',
            documentContent: docContent,
            documentCleanContent: docContent,
            voiceDirection: session.voice_direction || null,
            dMode: session.session_context?.phase === 'drafting' ? 'draft' : 'gatekeeper',
            hasInitialDraftRun: turns.length > 0,  // If has turns, initial draft already ran
            gatekeeperTurns: turns,
            showSessionPicker: false,
            showFoolscapPicker: false,
          })
        } catch (error) {
          console.error('[d-store] Failed to load session:', error)
          set({ error: 'Failed to load session' })
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await apiDeleteDSession(sessionId)
          const { sessions, sessionId: currentSessionId } = get()
          set({
            sessions: sessions.filter(s => s.session_id !== sessionId),
          })
          // If we deleted the current session, start a new one
          if (currentSessionId === sessionId) {
            get().startNewSession()
          }
        } catch (error) {
          console.error('[d-store] Failed to delete session:', error)
          set({ error: 'Failed to delete session' })
        }
      },

      renameSession: async (name: string) => {
        const { sessionId } = get()
        if (!sessionId) return
        try {
          await updateDSession(sessionId, { name })
          set({ sessionName: name })
          // Refresh sessions list
          get().fetchSessions()
        } catch (error) {
          console.error('[d-store] Failed to rename session:', error)
        }
      },

      // Foolscap selection actions
      openFoolscapPicker: () => set({ showFoolscapPicker: true }),
      closeFoolscapPicker: () => set({ showFoolscapPicker: false }),

      fetchAvailableFoolscaps: async () => {
        console.log('[d-store] Fetching available foolscaps...')
        set({ isLoadingFoolscaps: true })
        try {
          const foolscaps = await listAvailableFoolscaps()
          console.log('[d-store] Fetched foolscaps:', foolscaps.length, foolscaps)
          set({ availableFoolscaps: foolscaps, isLoadingFoolscaps: false })
        } catch (error) {
          console.error('[d-store] Failed to fetch foolscaps:', error)
          set({ availableFoolscaps: [], isLoadingFoolscaps: false })
        }
      },

      selectFoolscap: (foolscapId: string) => {
        const { availableFoolscaps } = get()
        const foolscap = availableFoolscaps.find(f => f.session_id === foolscapId)
        if (foolscap) {
          set({
            selectedFoolscapId: foolscapId,
            foolscapSnapshot: foolscap.foolscap_markdown || '',
          })
        }
      },

      createSessionFromFoolscap: async (foolscapId: string, name?: string, drafterSpec?: DrafterSpec) => {
        try {
          const { availableFoolscaps } = get()
          const foolscap = availableFoolscaps.find(f => f.session_id === foolscapId)

          const session = await createDSession(foolscapId, name, drafterSpec)

          // Create hidden initial turn that triggers the workflow
          const initialTurn: Turn = {
            role: 'user',
            content: '',  // Empty content - the Foolscap provides all context
            timestamp: new Date().toISOString(),
            isHidden: true,  // Don't show in UI
          }

          set({
            sessionId: session.session_id,
            sessionName: session.name || foolscap?.name || 'New Draft',
            selectedFoolscapId: foolscapId,
            foolscapSnapshot: session.foolscap_snapshot || foolscap?.foolscap_markdown || '',
            documentContent: '',
            documentCleanContent: '',
            voiceDirection: null,
            dMode: 'draft',  // Skip gatekeeper, go straight to draft mode
            hasInitialDraftRun: false,  // Will be set true after trigger
            pendingInitialTrigger: true,  // Signal DChat to trigger workflow
            workflowStatus: 'idle',  // Reset workflow status for new session
            gatekeeperTurns: [initialTurn],  // Start with hidden initial turn
            showFoolscapPicker: false,
            showSessionPicker: false,
          })
          // Refresh sessions list
          get().fetchSessions()
        } catch (error) {
          console.error('[d-store] Failed to create session from foolscap:', error)
          set({ error: 'Failed to create draft session' })
        }
      },

      // Document actions
      updateDocumentContent: (content: string) => set({ documentContent: content }),

      // Voice direction actions
      setVoiceDirection: (direction: VoiceDirection) => set({ voiceDirection: direction }),

      // Gatekeeper actions
      addGatekeeperUserTurn: (content: string) => set((state) => ({
        gatekeeperTurns: [
          ...state.gatekeeperTurns,
          {
            role: 'user' as const,
            content,
            timestamp: new Date().toISOString(),
          },
        ],
      })),

      startGatekeeperStreaming: () => set({
        workflowStatus: 'streaming',
        gatekeeperStreamingContent: { response: '' },
        gatekeeperStreamingThinking: null,
        gatekeeperIsThinking: false,
      }),

      startGatekeeperThinking: () => set({
        gatekeeperIsThinking: true,
        gatekeeperStreamingThinking: '',
      }),

      updateGatekeeperThinking: (delta: string) => set((state) => ({
        gatekeeperStreamingThinking: (state.gatekeeperStreamingThinking || '') + delta,
      })),

      finishGatekeeperThinking: () => set({ gatekeeperIsThinking: false }),

      finishGatekeeperStreaming: (output: GatekeeperStreamingContent) => set((state) => ({
        workflowStatus: 'complete',
        gatekeeperTurns: [
          ...state.gatekeeperTurns,
          {
            role: 'assistant' as const,
            content: output.response,
            timestamp: new Date().toISOString(),
            thinking: state.gatekeeperStreamingThinking || undefined,
          },
        ],
        gatekeeperStreamingContent: null,
        gatekeeperStreamingThinking: null,
      })),

      // Zinsser actions
      startZinsserProcessing: () => set({
        zinsserIsProcessing: true,
        zinsserStreamingThinking: null,
        zinsserIsThinking: false,
        zinsserHasThinking: false,
      }),

      startZinsserThinking: () => set({
        zinsserIsThinking: true,
        zinsserStreamingThinking: '',
        zinsserHasThinking: true,
      }),

      updateZinsserThinking: (delta: string) => set((state) => ({
        zinsserStreamingThinking: (state.zinsserStreamingThinking || '') + delta,
      })),

      finishZinsserThinking: () => set({ zinsserIsThinking: false }),

      finishZinsserProcessing: (output) => set((state) => {
        // Update the last assistant turn with Zinsser thinking
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            zinsserThinking: state.zinsserStreamingThinking || undefined,
            zinsserRan: true,
          }
        }
        return {
          zinsserIsProcessing: false,
          zinsserDoc: output.zin_doc,
          zinsserDiagnosis: output.diagnosis,
          zinsserChangesMade: output.changes_made,
          documentContent: output.zin_doc,  // Update document with Zinsser output
          gatekeeperTurns: turns,
        }
      }),

      // StrengthenVerbs actions
      startStrengthenVerbsProcessing: () => set({
        strengthenVerbsIsProcessing: true,
        strengthenVerbsStreamingThinking: null,
        strengthenVerbsIsThinking: false,
        strengthenVerbsHasThinking: false,
      }),

      startStrengthenVerbsThinking: () => set({
        strengthenVerbsIsThinking: true,
        strengthenVerbsStreamingThinking: '',
        strengthenVerbsHasThinking: true,
      }),

      updateStrengthenVerbsThinking: (delta: string) => set((state) => ({
        strengthenVerbsStreamingThinking: (state.strengthenVerbsStreamingThinking || '') + delta,
      })),

      finishStrengthenVerbsThinking: () => set({ strengthenVerbsIsThinking: false }),

      finishStrengthenVerbsProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
            strengthenVerbsRan: true,
          }
        }
        return {
          strengthenVerbsIsProcessing: false,
          strengthenVerbsChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // CheckLogic actions
      startCheckLogicProcessing: () => set({
        checkLogicIsProcessing: true,
        checkLogicStreamingThinking: null,
        checkLogicIsThinking: false,
        checkLogicHasThinking: false,
      }),

      startCheckLogicThinking: () => set({
        checkLogicIsThinking: true,
        checkLogicStreamingThinking: '',
        checkLogicHasThinking: true,
      }),

      updateCheckLogicThinking: (delta: string) => set((state) => ({
        checkLogicStreamingThinking: (state.checkLogicStreamingThinking || '') + delta,
      })),

      finishCheckLogicThinking: () => set({ checkLogicIsThinking: false }),

      finishCheckLogicProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            checkLogicThinking: state.checkLogicStreamingThinking || undefined,
            checkLogicRan: true,
          }
        }
        return {
          checkLogicIsProcessing: false,
          checkLogicChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // RhythmSound actions
      startRhythmSoundProcessing: () => set({
        rhythmSoundIsProcessing: true,
        rhythmSoundStreamingThinking: null,
        rhythmSoundIsThinking: false,
        rhythmSoundHasThinking: false,
      }),

      startRhythmSoundThinking: () => set({
        rhythmSoundIsThinking: true,
        rhythmSoundStreamingThinking: '',
        rhythmSoundHasThinking: true,
      }),

      updateRhythmSoundThinking: (delta: string) => set((state) => ({
        rhythmSoundStreamingThinking: (state.rhythmSoundStreamingThinking || '') + delta,
      })),

      finishRhythmSoundThinking: () => set({ rhythmSoundIsThinking: false }),

      finishRhythmSoundProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            rhythmSoundThinking: state.rhythmSoundStreamingThinking || undefined,
            rhythmSoundRan: true,
          }
        }
        return {
          rhythmSoundIsProcessing: false,
          rhythmSoundChangesMade: output.changes_made,
          documentContent: output.zin_doc,
          gatekeeperTurns: turns,
        }
      }),

      // Responder actions
      startResponderProcessing: () => set({
        responderIsProcessing: true,
        responderStreamingThinking: null,
        responderIsThinking: false,
        responderHasThinking: false,
      }),

      startResponderThinking: () => set({
        responderIsThinking: true,
        responderStreamingThinking: '',
        responderHasThinking: true,
      }),

      updateResponderThinking: (delta: string) => set((state) => ({
        responderStreamingThinking: (state.responderStreamingThinking || '') + delta,
      })),

      finishResponderThinking: () => set({ responderIsThinking: false }),

      finishResponderProcessing: (output) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        const responderContent = output.response || ''

        // Check if we have an assistant turn to append to
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          // Append the responder response to the existing assistant turn
          const existingContent = turns[turns.length - 1].content || ''

          // Only append if there's a responder response
          const newContent = responderContent
            ? (existingContent ? `${existingContent}\n\n---\n\n${responderContent}` : responderContent)
            : existingContent

          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            content: newContent,
            responderThinking: state.responderStreamingThinking || undefined,
            responderRan: true,
          }
        } else if (responderContent) {
          // No assistant turn exists yet (D workflow skips Gatekeeper)
          // Create a new assistant turn with the responder response
          turns.push({
            role: 'assistant' as const,
            content: responderContent,
            timestamp: new Date().toISOString(),
            responderThinking: state.responderStreamingThinking || undefined,
            responderRan: true,
            // Mark thinking from editing passes
            zinsserThinking: state.zinsserStreamingThinking || undefined,
            zinsserRan: state.zinsserHasThinking,
            strengthenVerbsThinking: state.strengthenVerbsStreamingThinking || undefined,
            strengthenVerbsRan: state.strengthenVerbsHasThinking,
            checkLogicThinking: state.checkLogicStreamingThinking || undefined,
            checkLogicRan: state.checkLogicHasThinking,
            rhythmSoundThinking: state.rhythmSoundStreamingThinking || undefined,
            rhythmSoundRan: state.rhythmSoundHasThinking,
          })
        }

        return {
          responderIsProcessing: false,
          responderResponse: output.response,
          responderFollowUpQuestion: output.follow_up_question || null,
          responderHasThinking: true,
          workflowStatus: 'complete',  // Mark workflow as complete when responder finishes
          gatekeeperTurns: turns,
        }
      }),

      // Final diff actions
      setFinalDiff: (diff) => set((state) => {
        // Store globally and attach to the last turn
        const turns = [...state.gatekeeperTurns]
        if (turns.length > 0 && turns[turns.length - 1].role === 'assistant') {
          turns[turns.length - 1] = {
            ...turns[turns.length - 1],
            finalDiff: diff,
            showFinalDiff: diff?.has_changes || false,
          }
        }
        return {
          finalDiff: diff,
          gatekeeperTurns: turns,
        }
      }),
      showFinalDiffViewer: () => set({ showFinalDiff: true }),
      hideFinalDiffViewer: () => set({ showFinalDiff: false }),
      hideTurnDiffViewer: (turnIndex: number) => set((state) => {
        const turns = [...state.gatekeeperTurns]
        if (turns[turnIndex]) {
          turns[turnIndex] = { ...turns[turnIndex], showFinalDiff: false }
        }
        return { gatekeeperTurns: turns }
      }),

      // Real-time canvas editing
      applyCanvasEdit: (oldStr: string, newStr: string) => set((state) => {
        const content = state.documentContent || ''
        const found = content.includes(oldStr)
        console.log('[applyCanvasEdit] Found match:', found, 'oldStr length:', oldStr.length, 'content length:', content.length)
        if (!found) {
          console.warn('[applyCanvasEdit] WARNING: old_str not found in documentContent')
          console.log('[applyCanvasEdit] old_str (first 100):', oldStr.slice(0, 100))
          console.log('[applyCanvasEdit] content (first 200):', content.slice(0, 200))
        }
        const newContent = content.replace(oldStr, newStr)
        return { documentContent: newContent }
      }),

      applyCanvasInsert: (insertLine: number, insertText: string) => set((state) => {
        const content = state.documentContent || ''
        const lines = content.split('\n')
        lines.splice(insertLine, 0, insertText)
        return { documentContent: lines.join('\n') }
      }),
    }),
    {
      name: 'd-store',
      partialize: (state) => ({
        // Persist session state
        sessionId: state.sessionId,
        sessionName: state.sessionName,
        selectedFoolscapId: state.selectedFoolscapId,
        foolscapSnapshot: state.foolscapSnapshot,
        dMode: state.dMode,
        hasInitialDraftRun: state.hasInitialDraftRun,
        documentContent: state.documentContent,
        documentCleanContent: state.documentCleanContent,
        voiceDirection: state.voiceDirection,
        gatekeeperTurns: state.gatekeeperTurns,
        zinsserDoc: state.zinsserDoc,
        zinsserDiagnosis: state.zinsserDiagnosis,
        // Don't persist streaming state, processing flags, or transient UI state
      }),
    }
  )
)
